import type { jsPDF } from 'jspdf';
import { GeoFeature, GeoFeatureCollection, GeoPosition, computeBounds, extractOuterRings, simplifyRing } from '../../utils/summary_report/geojsonUtils';
import { colors, RGB } from '../../theme/themes';
import { RiskBand } from '../../types/types';
import { Rect } from '../../utils/summary_report/pdfPrimitives';

const BAND_FILL: Record<RiskBand, RGB> = {
  extreme: colors.extreme700,
  high: colors.high700,
  medium: colors.medium700,
  low: colors.low700,
};

/** Below this on-page size (points), a filled polygon is too small to read
 * — draw a marker dot at its centroid instead/in addition. Tuned for
 * small Mogadishu wards (~0.02°) rendering alongside large rural districts
 * (~1°+) on the same whole-country map. */
const MIN_VISIBLE_SIZE_PT = 4;

export interface DistrictMapOptions {
  rect: Rect;
  featureCollection: GeoFeatureCollection;
  /** Property on each feature holding the district name (default 'shapeName'). */
  nameProperty?: string;
  /** Returns this district's risk band, or null if it has no risk data on record. */
  getBand: (districtName: string) => RiskBand | null;
  simplifyToleranceDeg?: number;
}

export interface DistrictMapResult {
  /** Risk districts that WERE found in the GeoJSON and shaded — caller
   * diffs this against its full risk dataset to find anything missing. */
  matchedDistricts: string[];
  /** Risk districts too small to read as a filled shape at this scale —
   * rendered as a marker dot instead. Informational only. */
  markerOnlyDistricts: string[];
}

function ringCentroid(ring: GeoPosition[]): [number, number] {
  let sumLon = 0, sumLat = 0;
  for (const [lon, lat] of ring) { sumLon += lon; sumLat += lat; }
  return [sumLon / ring.length, sumLat / ring.length];
}

interface MarkerEntry {
  x: number;
  y: number;
  color: RGB;
  name: string;
}

/**
 * Small, dense districts (e.g. Mogadishu wards) can have centroids only a
 * fraction of a point apart on a whole-country map — well inside a single
 * marker's radius, so without this the last marker drawn simply hides the
 * rest. This groups markers within `minSeparation` of each other and fans
 * them radially around their shared centroid, returning both the new
 * position and the original (for a thin leader line back to the true spot).
 */
function declutterMarkers(
  markers: MarkerEntry[],
  minSeparation: number,
): Array<MarkerEntry & { anchorX: number; anchorY: number; clustered: boolean; angle: number }> {
  const used = new Array(markers.length).fill(false);
  const clusters: MarkerEntry[][] = [];

  for (let i = 0; i < markers.length; i++) {
    if (used[i]) continue;
    const cluster = [markers[i]];
    used[i] = true;
    for (let j = i + 1; j < markers.length; j++) {
      if (used[j]) continue;
      const closeToCluster = cluster.some(
        (m) => Math.hypot(m.x - markers[j].x, m.y - markers[j].y) < minSeparation,
      );
      if (closeToCluster) {
        cluster.push(markers[j]);
        used[j] = true;
      }
    }
    clusters.push(cluster);
  }

  const result: Array<MarkerEntry & { anchorX: number; anchorY: number; clustered: boolean; angle: number }> = [];
  for (const cluster of clusters) {
    if (cluster.length === 1) {
      const m = cluster[0];
      result.push({ ...m, anchorX: m.x, anchorY: m.y, clustered: false, angle: 0 });
      continue;
    }
    const cx = cluster.reduce((s, m) => s + m.x, 0) / cluster.length;
    const cy = cluster.reduce((s, m) => s + m.y, 0) / cluster.length;
    // Radius large enough that adjacent fanned markers (with halo) don't touch:
    // chord between neighbors on an N-point circle is 2R·sin(π/N).
    const fanRadius = Math.max(9, minSeparation);
    cluster.forEach((m, idx) => {
      const angle = (2 * Math.PI * idx) / cluster.length - Math.PI / 2;
      result.push({
        ...m,
        x: cx + fanRadius * Math.cos(angle),
        y: cy + fanRadius * Math.sin(angle),
        anchorX: m.x,
        anchorY: m.y,
        clustered: true,
        angle,
      });
    });
  }
  return result;
}

/**
 * Draws every district in the GeoJSON, scaled/centered to fill `rect`
 * while preserving aspect ratio (no stretching). Districts with a known
 * risk band are filled with that band's color; everything else renders in
 * a light neutral "no data" fill so the shaded districts read in context
 * against the full country outline rather than floating in isolation.
 * Districts too small to see as a filled shape at this scale (small urban
 * wards next to large rural districts) get a marker dot at their centroid
 * instead, so they're never silently invisible.
 */
export function drawDistrictMap(doc: jsPDF, opts: DistrictMapOptions): DistrictMapResult {
  const {
    rect,
    featureCollection,
    nameProperty = 'shapeName',
    getBand,
    simplifyToleranceDeg = 0.012,
  } = opts;

  const features = featureCollection.features;
  const bounds = computeBounds(features);
  const lonSpan = bounds.maxLon - bounds.minLon;
  const latSpan = bounds.maxLat - bounds.minLat;

  // Fit-and-center: scale by whichever axis is the tighter constraint, then
  // center the remainder so the map never stretches to fill a mismatched
  // aspect ratio.
  const margin = 0.97;
  const scale = Math.min(rect.width / lonSpan, rect.height / latSpan) * margin;
  const drawnWidth = lonSpan * scale;
  const drawnHeight = latSpan * scale;
  const offsetX = rect.x + (rect.width - drawnWidth) / 2;
  const offsetY = rect.y + (rect.height - drawnHeight) / 2;

  const project = (lon: number, lat: number): [number, number] => [
    offsetX + (lon - bounds.minLon) * scale,
    offsetY + (bounds.maxLat - lat) * scale, // flip: higher latitude = smaller y (north-up)
  ];

  doc.setLineWidth(0.35);

  const matchedDistricts: string[] = [];
  const markerQueue: MarkerEntry[] = [];

  const drawFeature = (feature: GeoFeature, fill: RGB, isRiskDistrict: boolean, name: string) => {
    doc.setFillColor(fill[0], fill[1], fill[2]);
    doc.setDrawColor(...colors.mapStroke);

    const outerRings = extractOuterRings(feature);
    let largestOnPageSpan = 0;

    for (const ring of outerRings) {
      const simplified = simplifyRing(ring, simplifyToleranceDeg);
      if (simplified.length < 3) continue;
      const projected = simplified.map(([lon, lat]) => project(lon, lat));

      const xs = projected.map((p) => p[0]);
      const ys = projected.map((p) => p[1]);
      const onPageSpan = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
      largestOnPageSpan = Math.max(largestOnPageSpan, onPageSpan);

      const [startX, startY] = projected[0];
      const deltas: [number, number][] = [];
      for (let i = 1; i < projected.length; i++) {
        deltas.push([projected[i][0] - projected[i - 1][0], projected[i][1] - projected[i - 1][1]]);
      }
      doc.lines(deltas, startX, startY, [1, 1], 'FD', true);
    }

    if (isRiskDistrict && largestOnPageSpan < MIN_VISIBLE_SIZE_PT && outerRings.length > 0) {
      const [cLon, cLat] = ringCentroid(outerRings[0]);
      const [cx, cy] = project(cLon, cLat);
      markerQueue.push({ x: cx, y: cy, color: fill, name });
    }
  };

  // No-data districts first, risk-banded districts drawn on top — keeps
  // shaded districts from ever being visually obscured by a neighbor.
  for (const feature of features) {
    const name = String(feature.properties[nameProperty] ?? '');
    if (!getBand(name)) drawFeature(feature, colors.mapNoData, false, name);
  }
  const markerOnlyDistricts: string[] = [];
  for (const feature of features) {
    const name = String(feature.properties[nameProperty] ?? '');
    const band = getBand(name);
    if (band) {
      const beforeCount = markerQueue.length;
      drawFeature(feature, BAND_FILL[band], true, name);
      matchedDistricts.push(name);
      if (markerQueue.length > beforeCount) markerOnlyDistricts.push(name);
    }
  }

  // Fan out any markers whose true centroids are too close together to
  // render as separate dots (e.g. adjacent small Mogadishu wards), with a
  // thin leader line back to each one's real position.
  const declustered = declutterMarkers(markerQueue, 7);

  for (const marker of declustered) {
    if (marker.clustered) {
      doc.setDrawColor(...colors.stone400);
      doc.setLineWidth(0.35);
      doc.line(marker.anchorX, marker.anchorY, marker.x, marker.y);
    }

    doc.setFillColor(...colors.mapStroke);
    doc.circle(marker.x, marker.y, 2.6, 'F'); // white halo for contrast against any fill color
    doc.setFillColor(marker.color[0], marker.color[1], marker.color[2]);
    doc.circle(marker.x, marker.y, 1.8, 'F');
  }

  // Labels only for fanned (clustered) markers — an isolated marker is
  // already unambiguous from the legend + top-5 list; overlapping ones
  // aren't, without a name to tell them apart. Label direction follows
  // each marker's own fan angle so labels radiate outward with their dots
  // instead of all defaulting to "place right" and colliding with each
  // other when dots end up close together.
  const rightEdge = rect.x + rect.width;
  const leftEdge = rect.x;
  declustered
    .filter((m) => m.clustered)
    .forEach((marker) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.5);
      doc.setTextColor(...colors.stone700);
      const labelWidth = doc.getTextWidth(marker.name);
      const dx = Math.cos(marker.angle);
      const dy = Math.sin(marker.angle);

      let labelX: number;
      let labelY: number;
      if (Math.abs(dx) > 0.35) {
        labelX = dx > 0 ? marker.x + 4 : marker.x - 4 - labelWidth;
        labelY = marker.y + 2;
      } else {
        labelX = marker.x - labelWidth / 2;
        labelY = dy < 0 ? marker.y - 5 : marker.y + 9;
      }
      labelX = Math.min(Math.max(labelX, leftEdge), rightEdge - labelWidth);
      doc.text(marker.name, labelX, labelY);
    });

  return { matchedDistricts, markerOnlyDistricts };
}